import {Dialog, DialogFooter, Form, FormButton, FormField} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';
import {z} from 'zod';

import {isNil} from 'ramda';

import {useDuplicateModelYearMutation} from '@dms/api/new-vehicle';

import type {ApiError, Nullish} from 'shared/web';

import {handleCatalogueError, isStaleCatalogueVersion, t} from '../utils/catalogueUi';

const duplicateSchema = z.object({code: z.string().min(1)});
type DuplicateValues = z.infer<typeof duplicateSchema>;

export type DuplicateModelYearTarget = {
  modelYearId: string;
  /** Optimistic aggregate version of the source model year. */
  version: number;
  modelName: string;
  /** Validity window the copy starts with: the source window, or today for a fresh one. */
  validFrom: string;
  validTo: string | null;
};

/**
 * Asks for the new model year code and duplicates the target. Callers own where the copy is
 * opened and which validity window it inherits; the request shape stays in one place.
 */
export function DuplicateModelYearDialog(props: {
  target: DuplicateModelYearTarget | Nullish;
  onClose: () => void;
  onDuplicated: (modelYearId: string) => void;
  /** Reload the source of `target.version`: a stale one fails every retry until it is refetched. */
  onStaleVersion: () => void;
}) {
  const [duplicateModelYear] = useDuplicateModelYearMutation();
  const submit = async (values: DuplicateValues) => {
    if (isNil(props.target)) {
      return;
    }
    await duplicateModelYear({
      modelYearId: props.target.modelYearId,
      duplicateModelYearRequestBody: {
        name: values.code,
        catalogueName: [props.target.modelName, values.code].filter(Boolean).join(' '),
        validFrom: props.target.validFrom,
        validTo: props.target.validTo,
        version: props.target.version,
      },
    })
      .unwrap()
      .then((created) => {
        props.onClose();
        props.onDuplicated(created.id);
      })
      .catch((error: ApiError) => {
        handleCatalogueError(error);
        if (isStaleCatalogueVersion(error)) {
          props.onClose();
          props.onStaleVersion();
        }
      });
  };

  return (
    <Dialog
      isOpen={!isNil(props.target)}
      title={t('entity.newVehicle.catalogue.duplicateModelYear', 'Duplicate model year')}
      onClose={props.onClose}
      withAdditionalFooter
    >
      <Form<DuplicateValues>
        experimentalZodSchema={duplicateSchema}
        defaultValues={{code: ''}}
        onSubmit={submit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="code"
              type="text"
              label={t('entity.newVehicle.catalogue.newCode', 'New code')}
              isRequired
            />
            <DialogFooter>
              <HStack justify="flex-end">
                <FormButton
                  control={control}
                  type="submit"
                  title={t('entity.newVehicle.catalogue.duplicate', 'Duplicate')}
                />
              </HStack>
            </DialogFooter>
          </VStack>
        )}
      </Form>
    </Dialog>
  );
}
